import {
  WSControlGroup,
  WSControlGroupProps,
  WSForm,
  WSSelect
} from "@wingspanhq/fe-component-library";
import React from "react";
import { IDynamicFieldProps, IFormFieldOption } from "../../types";
import { fieldLabel, isFieldRequired } from "../../utils/fieldRules";

const ENUM_RADIO_THRESHOLD = 5;

// WSSelect renders an option's secondary helper line via `labels` (joined
// sublabel row); WSControlGroup takes it as `description`. Map the wire
// option's optional `description` onto each accordingly.
const toSelectOptions = (options: IFormFieldOption[]) =>
  options.map(option => ({
    value: option.value,
    label: option.label,
    ...(option.description ? { labels: [option.description] } : {})
  }));

const toControlOptions = (options: IFormFieldOption[]) =>
  options.map(option => ({
    value: option.value,
    label: option.label,
    description: option.description
  }));

// WSControlGroup's checkbox variant binds a `{ [optionValue]: boolean }`
// record, but a multi Enum's wire/schema value is `string[]` (seeded as [] and
// validated with a Yup array). This adapter converts at the component boundary
// so RHF, validation, and the submit payload all keep the array shape.
type CheckboxArrayGroupProps = Omit<
  Extract<WSControlGroupProps, { type: "checkbox" | "switch" }>,
  "type" | "value" | "onChange"
> & {
  value?: string[];
  onChange: (value: string[]) => void;
};

const WSControlGroupCheckboxArray: React.FC<CheckboxArrayGroupProps> = ({
  value,
  onChange,
  options,
  ...rest
}) => {
  const selected = Array.isArray(value) ? value : [];
  const record: Record<string, boolean> = {};
  options.forEach(option => {
    record[option.value] = selected.includes(option.value);
  });
  return (
    <WSControlGroup
      {...rest}
      type="checkbox"
      options={options}
      value={record}
      onChange={next =>
        // Emit in option order so the payload is deterministic.
        onChange(options.filter(o => next[o.value]).map(o => o.value))
      }
    />
  );
};

export const DynamicFieldEnum: React.FC<IDynamicFieldProps> = ({
  field,
  name,
  disabled,
  defaultValue
}) => {
  const options = field.options ?? [];
  const required = isFieldRequired(field);
  const description = field.description ?? field.ui?.helpText;
  // Prefer the server-specified UI component; otherwise fall back to a radio
  // group for short option lists and a select for longer ones.
  const useRadio =
    !field.multi &&
    (field.ui?.component === "RadioGroup" ||
      (field.ui?.component !== "Select" &&
        options.length < ENUM_RADIO_THRESHOLD));

  if (field.multi) {
    return (
      <WSForm.Field
        name={name}
        defaultValue={defaultValue}
        label={fieldLabel(field)}
        description={description}
        component={WSControlGroupCheckboxArray}
        componentProps={{
          size: "S",
          options: toControlOptions(options),
          disabled,
          required
        }}
      />
    );
  }

  if (useRadio) {
    return (
      <WSForm.Field
        name={name}
        defaultValue={defaultValue}
        label={fieldLabel(field)}
        description={description}
        component={WSControlGroup}
        componentProps={{
          type: "radio",
          size: "S",
          options: toControlOptions(options),
          disabled,
          required,
          chip: true
        }}
      />
    );
  }

  return (
    <WSForm.Field
      name={name}
      defaultValue={defaultValue}
      label={field.label}
      description={description}
      component={WSSelect}
      componentProps={{
        placeholder: field.ui?.placeholder ?? field.label,
        options: toSelectOptions(options),
        disabled,
        required,
        internalSearch: options.length >= 8
      }}
    />
  );
};
